import { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, Copy, ShieldOff, ShieldCheck, Plug } from 'lucide-react';
import { admin, getErrorMessage } from '../../utils/api';

export default function AdminDrlpApi({ user, onLogout }) {
  const [drlps, setDrlps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState(null); // {name, api_key}

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await admin.listDrlps();
      setDrlps(res.data.drlps || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load DRLPs'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const issue = async (d) => {
    try {
      const res = await admin.issueDrlpKey(d.id, `${d.name} key`);
      setNewKey({ name: d.name, api_key: res.data.api_key });
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to issue key')); }
  };

  const revoke = async (d) => {
    try {
      await admin.revokeDrlpKey(d.id);
      toast.success(`Revoked API key for ${d.name}`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to revoke key')); }
  };

  const copyKey = () => {
    if (newKey?.api_key) {
      navigator.clipboard.writeText(newKey.api_key);
      toast.success('API key copied');
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-drlp-api-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plug className="w-6 h-6 text-teal-700" /> DRLP API Access
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Issue and rotate per-retailer <code>X-DRLP-API-Key</code> credentials for catalog,
            inventory, and pricing ingestion. Keys are shown once and stored hashed.
          </p>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Retailer API Keys</CardTitle>
              <CardDescription>Rotate re-issues a fresh key and immediately invalidates the previous one.</CardDescription>
            </div>
            <Button variant="outline" onClick={load} data-testid="drlp-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
            ) : drlps.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="drlp-empty">No DRLP retailers found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Retailer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>API Key</TableHead>
                    <TableHead>Push</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drlps.map((d) => (
                    <TableRow key={d.id} data-testid={`drlp-row-${d.id}`}>
                      <TableCell className="font-medium">{d.name}<div className="text-xs text-gray-400">{d.email}</div></TableCell>
                      <TableCell><Badge variant="outline">{d.store_status || 'unknown'}</Badge></TableCell>
                      <TableCell>
                        {d.api_key?.active ? (
                          <span className="flex items-center gap-1 text-emerald-700 text-sm">
                            <ShieldCheck className="w-4 h-4" /> {d.api_key.prefix}…
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">No active key</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.push_enabled
                          ? <Badge className="bg-blue-100 text-blue-800">Enabled</Badge>
                          : <span className="text-xs text-gray-400">Off</span>}
                      </TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => issue(d)} data-testid={`drlp-issue-${d.id}`}>
                          <KeyRound className="w-3 h-3 mr-1" />{d.api_key?.active ? 'Rotate' : 'Issue'}
                        </Button>
                        {d.api_key?.active && (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => revoke(d)} data-testid={`drlp-revoke-${d.id}`}>
                            <ShieldOff className="w-3 h-3 mr-1" />Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
          <DialogContent data-testid="drlp-newkey-dialog">
            <DialogHeader>
              <DialogTitle>API Key for {newKey?.name}</DialogTitle>
              <DialogDescription>
                Copy this key now — it is shown only once and cannot be retrieved later.
                Share it securely with the retailer.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-gray-900 text-emerald-300 font-mono text-sm rounded-lg p-3 break-all" data-testid="drlp-newkey-value">
              {newKey?.api_key}
            </div>
            <DialogFooter>
              <Button onClick={copyKey} data-testid="drlp-copy-key-btn"><Copy className="w-4 h-4 mr-2" />Copy Key</Button>
              <Button variant="outline" onClick={() => setNewKey(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
